import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import { channelMessageAttachmentPath, formatOperatorName } from '@onr/schema'
import { captureException } from '@sentry/react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { doc, getFirestore, serverTimestamp, writeBatch } from 'firebase/firestore'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { toast } from '@/components/molecules/Toaster/useToast'
import { type ChannelViewProps } from '@/components/templates/ChannelView'
import { DashboardAlertContext } from '@/model/contexts/DashboardAlertContext'
import { collections } from '@/schema'
import { channelDeletionsQuery, channelMessagesQuery } from '@/state/channels/channel'
import { useChannelModeration } from '@/state/channels/useChannelModeration'
import { useChannelPreferences } from '@/state/channels/useChannelPreferences'
import { useChannelUnread } from '@/state/channels/useChannelUnread'
import { uploadVoiceClip } from '@/state/firebase-storage/uploadVoiceClip'
import { regionQuery } from '@/state/regions/region'
import { dashboardRegionTeamQuery } from '@/state/teams/teams'
import { userQuery } from '@/state/users/user'

import { channelMuteLabel, channelName } from './channelLabels'
import { toChannelMessageItems } from './channelMessageItems'
import { useMessagingRail } from './useMessagingRail'

export const useChannelPage = (): ChannelViewProps => {
  const { regionId, channelId } = useParams({ from: '/dashboard/$regionId/messages/channels/$channelId' })
  const intl = useIntl()
  const { firebaseUser, regionId: moderatorRegionId } = useAuthenticatedFirebaseUser()
  const { isSysAdmin, canModerate } = useChannelModeration(regionId)
  const region = useSuspenseQuery(regionQuery(regionId))
  const { data: teams } = useSuspenseQuery(dashboardRegionTeamQuery(region.data.data))
  const eveningId = region.data.data?.currentOperatingDate?.eveningId
  const eveningOpen = eveningId !== undefined
  const { data: messages } = useQuery(channelMessagesQuery(regionId, channelId, eveningId))
  const { data: deletions } = useQuery(channelDeletionsQuery(regionId, channelId, eveningId, isSysAdmin))
  const { muted, toggleMute } = useChannelPreferences(channelId)
  const { alerts: contextAlerts, dismissAlert } = useContext(DashboardAlertContext)
  const hasUnread = useChannelUnread(regionId, channelId, eveningId)

  const regionName = region.data.data?.name ?? ''
  const { uid, displayName } = firebaseUser
  const { data: user } = useQuery(userQuery(uid))
  const readCursor = user?.data?.channels?.[channelId]
  const newestMessageAt = messages?.data[0]?.createdAt

  const rail = useMessagingRail({
    regionId,
    channelId,
    eveningId,
    teamsHaveUnread: teams.data.some((team) => team.messagingStatus.hasUnreadTeamMessages),
    destination: 'channel',
    teamsTo: '/dashboard/$regionId/messages',
    teamsParams: { regionId },
    channelTo: '/dashboard/$regionId/messages/channels/$channelId',
    channelParams: { regionId, channelId },
  })

  // Alerts for messages read straight from the open thread are dismissed here, or they would pop up
  // as toasts the moment the operator leaves the channel.
  useEffect(() => {
    contextAlerts
      .filter((alert) => alert.type === 'channelMessage' && alert.channelId === channelId && !alert.isDismissed)
      .forEach((alert) => {
        dismissAlert(alert._id)
      })
  }, [contextAlerts, dismissAlert, channelId])

  // Reading the thread is what clears the dot, so every message landing while the page is open is
  // marked read right away — but the write goes to users/{uid}, which every station watches live,
  // so it only happens when the stored cursor is actually behind the thread.
  const markedReadAt = useRef(0)
  useEffect(() => {
    if (eveningId === undefined || !newestMessageAt) {
      return
    }
    const newest = newestMessageAt.getTime()
    const stored = readCursor?.lastReadEveningId === eveningId ? (readCursor?.lastReadAt?.getTime() ?? 0) : 0
    // The ref carries the guard between the write and its echo: serverTimestamp() reads back as
    // null in the local snapshot, so the stored cursor alone would let the effect write again.
    if (Math.max(stored, markedReadAt.current) >= newest) {
      return
    }
    markedReadAt.current = newest
    collections.users
      .update(uid, {
        [`channels.${channelId}.lastReadEveningId`]: eveningId,
        [`channels.${channelId}.lastReadAt`]: serverTimestamp(),
      })
      .catch(() => {
        markedReadAt.current = 0
      })
  }, [uid, channelId, eveningId, newestMessageAt, readCursor])

  const messageItems = useMemo(
    () =>
      toChannelMessageItems({
        messages: messages?.data ?? [],
        teams: teams.data,
        intl,
        regionName,
        deletions: deletions?.data,
      }),
    [messages?.data, teams.data, intl, regionName, deletions?.data]
  )

  const writeChannelMessage = useCallback(
    async (
      openEveningId: number,
      content: (messageId: string) => Promise<{ text: string } | { audioStoragePath: string; audioDurationMs: number }>
    ): Promise<{ committed: Promise<void> }> => {
      const batch = writeBatch(getFirestore())
      const { evenings } = collections.regions(regionId).channels(channelId)
      const messageRef = doc(evenings(String(openEveningId)).messages.write.collection())

      // A client date, not serverTimestamp(): the local echo of a server sentinel reads back as
      // null, which the required createdAt rejects, and the converter then blanks the whole thread.
      batch.set(messageRef, {
        sender: 'admin',
        senderId: uid,
        senderName: formatOperatorName(displayName),
        // A super admin writes as the national office whatever central their account belongs to;
        // the rule pins the flag to the claim.
        ...(isSysAdmin ? { senderSysAdmin: true } : {}),
        ...(await content(messageRef.id)),
        createdAt: new Date(),
      })
      // Server clock on both sides of the unread comparison: an operator station running a few
      // minutes fast would otherwise never see the dot light up.
      batch.set(
        evenings.write.doc(String(openEveningId)),
        { regionId, channelId, eveningId: openEveningId, lastMessageAt: serverTimestamp(), lastMessageSenderId: uid },
        { merge: true }
      )

      const committed = batch.commit()
      // Update the user's read cursor as best-effort; not in the atomic batch to avoid NOT_FOUND
      // errors if users/{uid} doesn't exist yet (created asynchronously by onCreateUser trigger).
      committed
        .then(() =>
          collections.users.update(uid, {
            [`channels.${channelId}.lastReadEveningId`]: openEveningId,
            [`channels.${channelId}.lastReadAt`]: serverTimestamp(),
          })
        )
        .catch(() => {})
      return { committed }
    },
    [regionId, channelId, uid, displayName, isSysAdmin]
  )

  const onSendMessage = useCallback(
    async (rawText: string) => {
      const text = rawText.trim()
      if (eveningId === undefined || !text) {
        return
      }
      const { committed } = await writeChannelMessage(eveningId, async () => ({ text }))
      await committed
    },
    [eveningId, writeChannelMessage]
  )

  // The clip goes up before the message exists: the storage rules refuse it once the message is written, and a
  // message pointing at a clip still uploading would show a dead player to the whole central.
  const onSendVoiceMessage = useCallback(
    async (blob: Blob, durationMs: number) => {
      if (eveningId === undefined) {
        throw new Error('Voice message not sent, the evening is closed')
      }
      const { committed } = await writeChannelMessage(eveningId, async (messageId) => ({
        audioStoragePath: await uploadVoiceClip(
          channelMessageAttachmentPath({ regionId, channelId, eveningId, messageId, kind: 'audio' }),
          blob
        ),
        audioDurationMs: durationMs,
      }))
      // The clip is uploaded, so the write is queued locally and reaches the thread at once; its promise only
      // settles on the server's acknowledgement, which a dropped connection delays.
      committed.catch((error: unknown) => {
        captureException(error, { tags: { 'channel.message': 'voice' } })
        toast({
          title: intl.formatMessage({ id: 'toast.broadcast.error.title' }),
          description: intl.formatMessage({ id: 'message.voice.sendFailed' }),
          variant: 'destructive',
        })
      })
    },
    [regionId, channelId, eveningId, writeChannelMessage, intl]
  )

  const onDeleteMessage = useCallback(
    async (messageId: string) => {
      if (eveningId === undefined) {
        return
      }
      await collections
        .regions(regionId)
        .channels(channelId)
        .evenings(String(eveningId))
        // Server clock, unlike the composer above: deletedAt is nullish in the schema, so the local
        // echo of the sentinel reads back as null without blanking the thread, and the rule refuses
        // any other value — a moderator cannot date their own trace. The central rides along because
        // the account does not carry it; the rule pins it to this operator's own claim.
        .messages.update(messageId, {
          deletedAt: serverTimestamp(),
          deletedBy: uid,
          deletedByRegionId: moderatorRegionId,
          ...(isSysAdmin ? { deletedBySysAdmin: true } : {}),
        })
    },
    [regionId, channelId, eveningId, uid, moderatorRegionId, isSysAdmin]
  )

  return {
    rail,
    channelName: channelName(intl, channelId),
    muteLabel: channelMuteLabel(intl, channelId, muted),
    regionName,
    piloteRegionId: region.data.data?.piloteId ?? 0,
    eveningOpen,
    hasUnread,
    messages: messageItems,
    muted,
    onToggleMute: toggleMute,
    onSendMessage,
    onSendVoiceMessage,
    canDelete: canModerate,
    onDeleteMessage: canModerate ? onDeleteMessage : undefined,
  }
}
